// src/components/Home.jsx
import { useMemo } from 'react';
import SeoHelmet from './SeoHelmet';

import Simulator from './Simulator';
import MangaList from './MangaList';
import VideoGallery from './VideoGallery';
import CabaIntroSection from './CabaIntroSection';
import SocialFollowBanner from './SocialFollowBanner';

import faqItems from '../data/faq';

/* ===== 日付ユーティリティ ===== */
const toDateStringLocal = (d = new Date()) => {
  const tzoffsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzoffsetMs).toISOString().slice(0, 10);
};
const toLocalEndOfDayISO = (d = new Date()) => {
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const tzoffsetMs = end.getTimezoneOffset() * 60000;
  return new Date(end.getTime() - tzoffsetMs).toISOString();
};
const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

/* ===== ページ定数 ===== */
const canonical = 'https://new-nightlife.jp/';
const ogImage = 'https://new-nightlife.jp/images/ogp/main.webp';
const pageTitle = '未経験特化キャバクラ入門サイト｜New-Nightlife';
const pageDesc =
  '未経験に特化。公式LINEで簡単相談、月収シミュレーター・動画・漫画でわかるキャバクラLP。';

const Home = () => {
  const { jsonLdJob, jsonLdBreadcrumb, jsonLdFaq, jsonLdArticle } = useMemo(() => {
    /* ===== JobPosting ===== */
    const jsonLdJob = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      '@id': `${canonical}#jobposting`,
      title: '未経験のための関東エリアのキャバクラ求人・体験入店情報【New-Nightlife】',
      description:
        '未経験歓迎。歌舞伎町・六本木・池袋など関東エリアのキャバクラ求人特集。勤務地多数＆高待遇。シミュレーターで収入もチェック可能。LINEで気軽に応募＆相談できます。',
      identifier: {
        '@type': 'PropertyValue',
        name: 'New-Nightlife',
        value: 'cabajob001',
      },
      datePosted: toDateStringLocal(new Date()),
      validThrough: toLocalEndOfDayISO(addDays(90)),
      employmentType: ['PART_TIME'],
      hiringOrganization: {
        '@type': 'Organization',
        name: 'New-Nightlife',
        url: canonical,
        sameAs: [canonical],
        logo: 'https://new-nightlife.jp/apple-touch-icon.png',
      },
      jobLocation: [
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '東京都', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '神奈川県', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '埼玉県', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '千葉県', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '栃木県', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '茨城県', addressCountry: 'JP' } },
        { '@type': 'Place', address: { '@type': 'PostalAddress', addressRegion: '群馬県', addressCountry: 'JP' } },
      ],
      jobBenefits: [
        '日払いOK',
        '他サイトよりも高時給',
        '昇給あり',
        'インセンティブあり',
        'シフト自由',
        '週1〜OK',
        '終電上がりOK',
        '体験入店あり',
        '衣装貸与あり',
        '髪型自由',
        'ネイルOK',
        '友達と応募OK',
        'ノルマなし',
        'ペナルティなし',
        '女性スタッフ在籍',
        'サポート体制万全',
        '送迎あり',
      ],
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'JPY',
        value: { '@type': 'QuantitativeValue', value: 4000, unitText: 'HOUR' },
      },
      directApply: true,
    };

    /* ===== パンくずリスト ===== */
    const jsonLdBreadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{ '@type': 'ListItem', position: 1, name: 'ホーム', item: canonical }],
    };

    /* ===== FAQ ===== */
    const jsonLdFaq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer },
      })),
    };

    /* ===== Article（任意・SEO強化） ===== */
    const jsonLdArticle = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: pageTitle,
      description: pageDesc,
      image: {
        '@type': 'ImageObject',
        url: ogImage,
        width: 1200,
        height: 630,
      },
      datePublished: '2024-04-01T00:00:00+09:00',
      dateModified: '2025-08-11T00:00:00+09:00',
      author: {
        '@type': 'Organization',
        name: 'New-Nightlife 編集部',
        url: 'https://new-nightlife.jp/about',
      },
      publisher: {
        '@type': 'Organization',
        name: 'New-Nightlife',
        logo: {
          '@type': 'ImageObject',
          url: 'https://new-nightlife.jp/apple-touch-icon.png',
        },
      },
    };

    return { jsonLdJob, jsonLdBreadcrumb, jsonLdFaq, jsonLdArticle };
  }, []);

  return (
    <>
      <SeoHelmet title={pageTitle} description={pageDesc} url={canonical} image={ogImage} />

      <script type="application/ld+json">{JSON.stringify(jsonLdJob)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
      <script id="faq-jsonld" type="application/ld+json">
        {JSON.stringify(jsonLdFaq)}
      </script>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>

      <main id="main" role="main" aria-labelledby="page-title">
        <h1 id="page-title" style={{ position: 'absolute', left: '-9999px' }}>
          {pageTitle}
        </h1>

        <section id="simulator-section" aria-label="時給シミュレーター">
          <Simulator />
        </section>
        <section id="manga-section" aria-label="漫画で流れを理解">
          <MangaList />
        </section>
        <section id="videos-section" aria-label="動画ギャラリー">
          <VideoGallery />
        </section>

        <SocialFollowBanner />
        <CabaIntroSection />
      </main>
    </>
  );
};

export default Home;
